import { COPPEL_BANCO_ID, coppelIntegracionActiva } from './constants'
import { esIdBancoCoppel, esNombreBancoCoppel } from './bancos'

export function esCoppelEntidad(entidad) {
  if (!coppelIntegracionActiva()) return false
  if (!entidad) return false
  const bancoId = entidad.BancoId != null ? entidad.BancoId : entidad.IdBanco
  if (esIdBancoCoppel(bancoId)) return true
  const nombre = entidad.Banco != null ? entidad.Banco : entidad.NombreBanco
  return esNombreBancoCoppel(nombre)
}

function bancoIdCoppel(entidad) {
  const bancoId = entidad.BancoId != null ? entidad.BancoId : entidad.IdBanco
  const id = parseInt(bancoId, 10)
  if (!Number.isNaN(id) && esIdBancoCoppel(id)) return id
  return COPPEL_BANCO_ID
}

function idIonCoppel(entidad) {
  if (!entidad) return ''
  return String(entidad.CoppelApplicationId || entidad.IdIon || '')
}

function esquemaCoppel(entidad) {
  const esquema = entidad.Esquema != null ? entidad.Esquema : entidad.Esquemas
  return parseInt(esquema, 10)
}

function tasasCoppelIguales(a, b) {
  return Math.abs(parseFloat(a) - parseFloat(b)) < 0.001
}

/** Plazo en meses (ítem API) o años (algunas respuestas SOC). */
function plazosCoppelIguales(plazoA, plazoB) {
  const a = parseInt(plazoA, 10)
  const b = parseInt(plazoB, 10)
  if (Number.isNaN(a) || Number.isNaN(b)) return false
  if (a === b) return true
  if (a > 0 && a <= 30 && b === a * 12) return true
  if (b > 0 && b <= 30 && a === b * 12) return true
  return false
}

function mensualidadesCoppelIguales(a, b) {
  const na = parseFloat(String(a).replace(/,/g, ''))
  const nb = parseFloat(String(b).replace(/,/g, ''))
  if (Number.isNaN(na) || Number.isNaN(nb)) return false
  return Math.abs(na - nb) < 1
}

/** Id de ResultadosPrecalculados en SOC (no confundir con variante de catálogo ni con Id de solicitud). */
export function idResultadoFavoritoCoppel(entidad) {
  if (!entidad) return null
  if (entidad.Resultado != null && entidad.Resultado !== '') {
    const resultado = parseInt(entidad.Resultado, 10)
    if (!Number.isNaN(resultado)) return resultado
  }
  if (entidad.Id != null && entidad.Id !== '') {
    const id = parseInt(entidad.Id, 10)
    if (!Number.isNaN(id)) return id
  }
  return null
}

/**
 * intVariante para BorrarSolicita (pestaña Productos).
 * Coppel comparte VarianteId catálogo (205) entre productos → enviar ResultadosPrecalculados.Id.
 * PHP usa objCaso.Resultado para borrar solo una fila (resolveSolicitudCoppelPorResultado).
 */
export function idVarianteBorrarSolicitudCoppel(solicitud) {
  if (!solicitud) return null
  if (solicitud.Resultado != null && solicitud.Resultado !== '') {
    const resultado = parseInt(solicitud.Resultado, 10)
    if (!Number.isNaN(resultado)) return resultado
  }
  return idResultadoFavoritoCoppel(solicitud)
}

/** Clave estable para comparar favoritos Coppel sin `item.Id` del motor SOC. */
export function claveFavoritoCoppel(item) {
  if (!esCoppelEntidad(item)) return ''
  return [
    bancoIdCoppel(item),
    item.Plazo,
    item.Tasa,
    esquemaCoppel(item),
    idIonCoppel(item),
  ].join('|')
}

/** Compara ítem cotizado vs favorito/solicitud guardada en SOC. */
export function esMismoFavoritoCoppel(item, favorito) {
  if (!item || !favorito) return false
  if (!esCoppelEntidad(favorito)) return false

  const itemIon = idIonCoppel(item)
  const favoritoIon = idIonCoppel(favorito)
  if (itemIon && favoritoIon && itemIon === favoritoIon) {
    return true
  }

  if (
    plazosCoppelIguales(favorito.Plazo, item.Plazo) &&
    tasasCoppelIguales(favorito.Tasa, item.Tasa) &&
    esquemaCoppel(favorito) === esquemaCoppel(item)
  ) {
    return true
  }

  return (
    plazosCoppelIguales(favorito.Plazo, item.Plazo) &&
    tasasCoppelIguales(favorito.Tasa, item.Tasa) &&
    mensualidadesCoppelIguales(favorito.Mensualidad, item.Mensualidad)
  )
}

function plazoMesesCoppel(entidad) {
  const p = parseInt(entidad.Plazo, 10)
  if (Number.isNaN(p)) return 0
  if (p > 0 && p <= 30) return p * 12
  return p
}

/** Clave estable para saber si un producto Coppel ya está en pestaña Productos. */
export function claveProductoCoppel(entidad) {
  if (!entidad) return ''
  const plazo = plazoMesesCoppel(entidad)
  const tasa = parseFloat(entidad.Tasa)
  const tasaKey = Number.isNaN(tasa) ? '0' : tasa.toFixed(2)
  const mens = parseFloat(
    String(
      entidad.MensualidadPromedio ||
      entidad.Mensualidad ||
      entidad.MensualidadInicial ||
      0
    ).replace(/,/g, '')
  )
  const mensKey = Number.isNaN(mens) ? '0' : String(Math.round(mens))
  return [plazo, tasaKey, mensKey].join('|')
}

/** ¿Este ítem Coppel ya está en caso.solicitudes (pestaña Productos)? */
export function estaCoppelEnSolicitudes(item, solicitudes) {
  if (!esCoppelEntidad(item) || !Array.isArray(solicitudes) || !solicitudes.length) {
    return false
  }
  const claveItem = claveProductoCoppel(item)
  if (!claveItem || claveItem === '0|0.00|0') return false
  return solicitudes.some(function(s) {
    return esCoppelEntidad(s) && claveProductoCoppel(s) === claveItem
  })
}

/** ¿La solicitud guardada en SOC corresponde a este ítem cotizado? */
export function esMismaSolicitudCoppel(item, solicitud) {
  if (!item || !solicitud || !esCoppelEntidad(solicitud)) return false
  return claveProductoCoppel(item) === claveProductoCoppel(solicitud)
}

/** ¿El ítem debe mostrarse como favorito en la UI? */
export function esFavoritoCoppelEnUI(item, favorito) {
  return esMismoFavoritoCoppel(item, favorito)
}

/** Localiza el favorito persistido en SOC para eliminarlo. */
export function buscarFavoritoCoppelGuardado(item, favoritos) {
  if (!item || !Array.isArray(favoritos)) return null
  return favoritos.find((f) => esMismoFavoritoCoppel(item, f)) || null
}
